import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, BadgeCheck, CreditCard, Timer, Users, Building2, Dumbbell, Baby, RotateCcw, Star } from "lucide-react";
import api from "../../lib/api";
import { useSite } from "../../contexts/SiteContext";
import { SPORTS, IMG } from "../../lib/sports";
import SiteLayout from "../../components/site/SiteLayout";
import { SearchBar } from "../../components/site/SearchBar";
import { CoachCard, Eyebrow } from "../../components/site/ui";

const Hero = () => {
  const { L } = useSite();
  return (
    <section className="px-2 sm:px-4 pt-2 sm:pt-4" data-testid="home-hero">
      <div className="relative rounded-[28px] sm:rounded-[36px] overflow-hidden bg-[#0A1628] min-h-[640px] lg:min-h-[760px] flex items-end">
        <img src={IMG.hero} alt="" className="absolute inset-0 w-full h-full object-cover object-[70%_center] otp-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-transparent" />
        <div className="relative w-full max-w-[1400px] mx-auto px-5 sm:px-10 pb-10 sm:pb-14 pt-28">
          <div className="otp-rise otp-d1"><Eyebrow light>{L("Now launching in Los Angeles", "Стартираме в Лос Анджелис")}</Eyebrow></div>
          <h1 className="otp-display uppercase text-white mt-5 text-5xl sm:text-6xl lg:text-7xl xl:text-[104px] max-w-5xl otp-rise otp-d2" data-testid="hero-headline">
            {L("Train with", "Тренирай с")}<br />{L("the best.", "най-добрите.")} <span className="italic text-[#60A5FA]">{L("Any sport.", "Всеки спорт.")}</span>
          </h1>
          <p className="mt-6 text-white/75 text-base sm:text-lg max-w-xl otp-rise otp-d3">{L("Private and group coaching across 10 sports. Find, book and pay in a few taps.", "Индивидуални и групови тренировки в 10 спорта. Намери, резервирай и плати с няколко докосвания.")}</p>
          <div className="mt-8 max-w-4xl otp-rise otp-d4"><SearchBar /></div>
          <div className="mt-7 flex flex-wrap gap-x-7 gap-y-3 text-white/80 text-sm otp-rise otp-d5" data-testid="hero-trust-row">
            <span className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#60A5FA]" /> {L("Coaches reviewed before going live", "Треньорите се одобряват преди публикуване")}</span>
            <span className="inline-flex items-center gap-2"><RotateCcw className="w-4 h-4 text-[#60A5FA]" /> {L("Free cancellation up to 24h", "Безплатна отмяна до 24ч.")}</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#60A5FA]" /> {L("Secure payments by Stripe", "Сигурни плащания чрез Stripe")}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => {
  const { sportLabel } = useSite();
  const row = [...SPORTS, ...SPORTS];
  return (
    <div className="overflow-hidden border-y border-slate-200 py-5 mt-10" data-testid="sports-marquee">
      <div className="otp-marquee">
        {row.map((s, i) => <span key={i} className="otp-display uppercase italic text-3xl sm:text-4xl text-[#0A1628] px-6 inline-flex items-center gap-6 whitespace-nowrap">{sportLabel(s.id)} <span className="w-2.5 h-2.5 rounded-full bg-[#1D4ED8]" /></span>)}
      </div>
    </div>
  );
};

export const SportTile = ({ s, i, count }) => {
  const { L, sportLabel } = useSite();
  return (
    <Link to={`/sports/${s.id}`} className="otp-tile group relative block aspect-[3/4] rounded-3xl overflow-hidden bg-[#0A1628]" data-testid={`sport-tile-${s.id}`}>
      <img src={s.img} alt={s.en} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/25 to-transparent" />
      <span className="absolute top-4 left-4 otp-label text-white/70">{String(i + 1).padStart(2, "0")}</span>
      <span className="otp-arrow absolute top-3 right-3 w-10 h-10 rounded-full bg-white text-[#0A1628] grid place-items-center"><ArrowRight className="w-4 h-4" /></span>
      <div className="absolute inset-x-4 bottom-4">
        <h3 className="otp-display uppercase text-white text-2xl sm:text-[28px] leading-[0.95]">{sportLabel(s.id)}</h3>
        <p className="mt-2 text-xs sm:text-sm text-white/70 hidden sm:block">{s.tag}</p>
        <p className="mt-2 text-xs font-semibold text-[#93C5FD]">{count ? `${count} ${L(count === 1 ? "coach" : "coaches", "треньори")}` : L("Explore coaches", "Виж треньори")}</p>
      </div>
    </Link>
  );
};

const Sports = () => {
  const { L, enabledSports } = useSite();
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/public/sport-stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const list = SPORTS.filter((s) => !enabledSports.length || enabledSports.includes(s.id));
  return (
    <section id="sports" className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-24" data-testid="home-sports">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <Eyebrow>{L(`${list.length} sports · one platform`, `${list.length} спорта · една платформа`)}</Eyebrow>
          <h2 className="otp-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl">{L("Every sport gets the main stage.", "Всеки спорт е на главната сцена.")}</h2>
        </div>
        <Link to="/find-coaches" className="otp-btn inline-flex items-center gap-2 font-semibold text-[#1D4ED8]" data-testid="sports-view-all">{L("Browse all coaches", "Всички треньори")} <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">{list.map((s, i) => <SportTile key={s.id} s={s} i={i} count={stats[s.id]} />)}</div>
    </section>
  );
};

const Featured = () => {
  const { L } = useSite();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/search/coaches", { params: { limit: 10, sort: "recommended" } }).then(({ data }) => setList(data.coaches)).catch(() => {}); }, []);
  if (!list.length) return <FoundingCoaches />;
  return (
    <section className="pt-28" data-testid="home-featured-coaches">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 flex items-end justify-between gap-6 mb-8">
        <div><Eyebrow>{L("On ONTHEPITCH", "В ONTHEPITCH")}</Eyebrow><h2 className="otp-display uppercase text-4xl sm:text-5xl mt-4">{L("Meet the coaches.", "Запознай се с треньорите.")}</h2></div>
        <Link to="/find-coaches" className="hidden sm:inline-flex font-semibold text-[#1D4ED8] items-center gap-2">{L("See all", "Виж всички")} <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="flex gap-5 overflow-x-auto otp-noscroll snap-x snap-mandatory px-4 sm:px-8 max-w-[1440px] mx-auto pb-4">
        {list.map((c) => <div key={c.id} className="snap-start shrink-0 w-[72%] sm:w-[300px]"><CoachCard c={c} /></div>)}
      </div>
    </section>
  );
};

const FoundingCoaches = () => {
  const { L, settings } = useSite();
  const perks = [
    [BadgeCheck, L("Free profile", "Безплатен профил"), L("No sign-up fees, no monthly plan.", "Без такси за регистрация и месечен план.")],
    [CreditCard, L(`Keep ${100 - (settings?.commission_pct ?? 10)}%`, `Задържаш ${100 - (settings?.commission_pct ?? 10)}%`), L("Secure card payments through Stripe.", "Сигурни картови плащания чрез Stripe.")],
    [Users, L("Private & group", "Индивидуални и групови"), L("Set your own prices, areas and hours.", "Ти определяш цени, райони и часове.")],
  ];
  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-28" data-testid="home-founding-coaches">
      <div className="rounded-[36px] border border-slate-200 p-8 sm:p-12 lg:p-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div>
          <Eyebrow>{L("Founding coaches · Los Angeles", "Първи треньори · Лос Анджелис")}</Eyebrow>
          <h2 className="otp-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#0A1628]">{L("Our first coaches are joining now.", "Първите треньори се присъединяват сега.")}</h2>
          <p className="mt-5 text-slate-600 max-w-xl">{L("We're hand-picking the coaches who launch ONTHEPITCH in Los Angeles. Every profile is reviewed by our team before it goes live, so families only see coaches we trust.", "Подбираме треньорите, с които стартираме в Лос Анджелис. Всеки профил се преглежда от екипа ни преди публикуване.")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register?role=coach" className="otp-btn otp-shine rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white px-7 py-4 font-semibold inline-flex items-center gap-2" data-testid="founding-apply-btn">{L("Apply as a founding coach", "Кандидатствай като треньор")} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/for-coaches" className="otp-btn rounded-full border-2 border-[#0A1628] text-[#0A1628] px-7 py-4 font-semibold" data-testid="founding-learn-btn">{L("How it works for coaches", "Как работи за треньори")}</Link>
          </div>
        </div>
        <div className="grid gap-3">
          {perks.map(([I, h, p]) => (
            <div key={h} className="flex gap-4 items-start rounded-3xl bg-[#F4F7FC] p-6">
              <span className="w-11 h-11 rounded-2xl bg-[#DBEAFE] text-[#1D4ED8] grid place-items-center shrink-0"><I className="w-5 h-5" /></span>
              <div><h3 className="font-semibold text-[#0A1628]">{h}</h3><p className="text-sm text-slate-600 mt-1">{p}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PhoneMock = () => {
  const { L } = useSite();
  return (
    <div className="relative mx-auto w-[290px] h-[590px] rounded-[44px] bg-[#0A1628] p-3 shadow-[0_40px_80px_-30px_rgba(29,78,216,.6)]" data-testid="phone-mockup" aria-hidden>
      <div className="w-full h-full rounded-[34px] bg-white overflow-hidden flex flex-col">
        <div className="relative h-56"><img src={IMG.parent} alt="" className="w-full h-full object-cover" /><span className="absolute top-3 left-3 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold inline-flex items-center gap-1"><Timer className="w-3 h-3 text-[#1D4ED8]" /> {L("Illustration", "Илюстрация")}</span></div>
        <div className="p-4 flex-1 flex flex-col">
          <div className="h-3 w-32 rounded bg-slate-200" /><div className="h-2.5 w-24 rounded bg-slate-100 mt-2" />
          <div className="mt-4 grid grid-cols-3 gap-1.5">{["9:00", "12:00", "17:00"].map((t, i) => <span key={t} className={`text-center text-xs py-2 rounded-xl border ${i === 0 ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "border-slate-200"}`}>{t}</span>)}</div>
          <div className="mt-4 rounded-2xl bg-slate-50 p-3 text-xs flex justify-between"><span>{L("1-on-1 · 60 min", "1:1 · 60 мин")}</span><span className="h-3 w-10 rounded bg-slate-200" /></div>
          <div className="mt-auto rounded-full bg-[#0A1628] text-white text-center py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"><CreditCard className="w-4 h-4" /> {L("Pay & confirm", "Плати и потвърди")}</div>
        </div>
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const { L } = useSite();
  const steps = [
    [L("Pick a sport & place", "Избери спорт и място"), L("Choose the sport, city or ZIP and a day. See coaches with real open slots.", "Избери спорт, град или пощ. код и ден. Виж треньори със свободни часове.")],
    [L("Choose your coach", "Избери треньор"), L("Compare prices, credentials and verified reviews from real sessions.", "Сравни цени, квалификации и проверени отзиви от реални тренировки.")],
    [L("Book & pay securely", "Резервирай и плати"), L("Pay by card through Stripe. Free cancellation up to 24h before.", "Плати с карта чрез Stripe. Безплатна отмяна до 24ч. преди.")],
  ];
  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-28" data-testid="home-how-it-works">
      <div className="rounded-[36px] bg-[#F4F7FC] p-6 sm:p-12 lg:p-16 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
        <div>
          <Eyebrow>{L("Booking in a few taps", "Резервация с няколко докосвания")}</Eyebrow>
          <h2 className="otp-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-4">{L("Three steps.", "Три стъпки.")}<br /><span className="text-[#1D4ED8] italic">{L("Game on.", "Започваме.")}</span></h2>
          <div className="mt-10 space-y-3">
            {steps.map(([h, p], i) => (
              <div key={h} className="flex gap-5 p-5 rounded-3xl bg-white border border-slate-200/70" data-testid={`how-step-${i + 1}`}>
                <span className="otp-display text-4xl text-[#1D4ED8] w-12 shrink-0">0{i + 1}</span>
                <div><h3 className="font-semibold text-lg text-[#0A1628]">{h}</h3><p className="text-slate-600 mt-1 text-sm sm:text-base">{p}</p></div>
              </div>
            ))}
          </div>
        </div>
        <PhoneMock />
      </div>
    </section>
  );
};

const Audiences = () => {
  const { L } = useSite();
  const cards = [
    [Baby, L("Parents", "Родители"), L("Book for your children from one account. Kids' details stay private.", "Резервирай за децата си от един профил. Данните им са защитени."), "/register?role=parent"],
    [Dumbbell, L("Athletes 18+", "Спортисти 18+"), L("Book and pay for your own sessions and track your progress.", "Резервирай и плащай за своите тренировки."), "/register?role=player"],
    [Users, L("Coaches", "Треньори"), L("Free profile, bookings, calendar and payments. You keep 90%.", "Безплатен профил, резервации, календар и плащания. Задържаш 90%."), "/register?role=coach"],
    [Building2, L("Academies", "Академии"), L("Run a roster of coaches, programs and group sessions under one brand.", "Управлявай треньори, програми и групови тренировки под един бранд."), "/for-coaches#academies"],
  ];
  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-28 grid lg:grid-cols-2 gap-4" data-testid="home-audiences">
      <div className="relative rounded-[36px] overflow-hidden min-h-[420px] bg-[#0A1628]">
        <img src={IMG.parent} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/30 to-transparent" />
        <div className="absolute bottom-0 p-8 sm:p-10"><Eyebrow light>{L("Built for every role", "За всяка роля")}</Eyebrow><h2 className="otp-display uppercase text-white text-4xl sm:text-5xl mt-4">{L("Made simple for the whole team.", "Просто за целия отбор.")}</h2></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {cards.map(([I, h, p, to], i) => (
          <Link key={h} to={to} className="otp-card rounded-[28px] border border-slate-200 p-7 flex flex-col" data-testid={`audience-card-${i}`}>
            <span className="w-12 h-12 rounded-2xl bg-[#DBEAFE] text-[#1D4ED8] grid place-items-center"><I className="w-6 h-6" /></span>
            <h3 className="otp-display uppercase text-2xl mt-8 text-[#0A1628]">{h}</h3>
            <p className="text-slate-600 mt-2 text-sm">{p}</p>
            <ArrowUpRight className="w-5 h-5 mt-auto pt-4 self-end box-content text-[#1D4ED8]" />
          </Link>
        ))}
      </div>
    </section>
  );
};

const Trust = () => {
  const { L } = useSite();
  const items = [
    [BadgeCheck, L("Reviewed coaches", "Одобрени треньори"), L("Every coach and academy is reviewed by our team before appearing in search. Credentials can be verified with documents.", "Всеки треньор и академия се преглежда преди да се появи в търсенето.")],
    [Star, L("Verified reviews only", "Само проверени отзиви"), L("Only customers with a completed session can leave a review.", "Само клиенти със завършена тренировка могат да оставят отзив.")],
    [RotateCcw, L("Clear cancellation", "Ясна политика"), L("Cancel 24h+ before for an automatic full refund. If a coach cancels, you're always refunded.", "Отмяна 24ч+ преди = автоматично пълно възстановяване. Ако треньорът отмени — винаги връщаме сумата.")],
  ];
  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-28" data-testid="home-trust">
      <h2 className="otp-display uppercase text-4xl sm:text-5xl max-w-2xl">{L("Trust is built in.", "Доверието е вградено.")}</h2>
      <div className="mt-10 grid md:grid-cols-3 gap-4">
        {items.map(([I, h, p]) => <div key={h} className="rounded-[28px] bg-[#F4F7FC] p-8"><I className="w-7 h-7 text-[#1D4ED8]" /><h3 className="font-semibold text-xl mt-6 text-[#0A1628]">{h}</h3><p className="mt-2 text-slate-600">{p}</p></div>)}
      </div>
    </section>
  );
};

const Expansion = () => {
  const { L } = useSite();
  const cities = [["Los Angeles", L("Launching", "Стартира"), true], ["San Diego", L("Next", "Следва")], ["Orange County", L("Next", "Следва")], ["New York", L("Planned", "Планиран")], ["Miami", L("Planned", "Планиран")], ["London", L("Planned", "Планиран")]];
  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-28" data-testid="home-expansion">
      <div className="relative rounded-[36px] bg-[#0A1628] text-white overflow-hidden otp-grain p-8 sm:p-14 lg:p-20">
        <div className="absolute -right-20 -top-20 w-[480px] h-[480px] rounded-full bg-[#1D4ED8]/40 blur-[120px]" />
        <div className="relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow light>{L("Starting in LA", "Стартираме в LA")}</Eyebrow>
            <h2 className="otp-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-4">{L("Born in Los Angeles. Built for everywhere.", "Роден в Лос Анджелис. Създаден за целия свят.")}</h2>
            <Link to="/register?role=coach" className="otp-btn otp-shine mt-10 inline-flex items-center gap-2 rounded-full bg-white text-[#0A1628] px-7 py-4 font-semibold" data-testid="expansion-coach-cta">{L("Coach with ONTHEPITCH", "Стани треньор в ONTHEPITCH")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {cities.map(([c, s, live]) => <li key={c} className="flex items-center justify-between py-4"><span className="otp-display uppercase text-2xl sm:text-3xl">{c}</span><span className={`text-xs font-semibold rounded-full px-3 py-1 ${live ? "bg-[#1D4ED8] text-white" : "bg-white/10 text-white/60"}`}>{s}</span></li>)}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <SiteLayout>
      <div data-testid="landing-page">
        <Hero /><Marquee /><Sports /><Featured /><HowItWorks /><Audiences /><Trust /><Expansion />
      </div>
    </SiteLayout>
  );
}
